import { Component, OnDestroy, OnInit } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { getNavItems, getAdminNavItems, filterNavByAccess } from './_nav'; // Import the functions
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { MobileNavActionItem, MobileNavLanguageItem } from './mobile-nav/mobile-nav.component';
import { AuthService } from '../../login/auth.service';
import { TokenStorageService } from '../../login/token-storage.service';
import { visibleAccountMenu } from './account-menu';
import { global_user } from '../../global/global';
import { cifTr, cifUs, cifEs, cifGr, cifDe } from '@coreui/icons';
import { OnboardingService, OnboardingStatus } from '../../services/onboarding.service';
import { MigrationModeService } from '../../services/migration-mode.service';
import { BackButtonService } from '../../services/back-button.service';
import { Capacitor } from '@capacitor/core';
import { ReleaseNewsItem, ReleaseNewsService } from '../../services/release-news.service';
import { ChannelUnreadService, withUnreadBadges } from '../../services/channel-unread.service';

@Component({
    selector: 'app-dashboard',
    templateUrl: './default-layout.component.html',
    styleUrls: ['./default-layout.component.scss'],
    standalone: false
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  public navItems: any = [];
  // The menu as built from access and onboarding, before the channel unread badges go on.
  private baseNavItems: any[] = [];
  private unreadCounts: Record<string, number> = {};
  /** Unread conversations across all channels, for the mobile menu button. */
  channelUnreadTotal = 0;
  private languageSubscription: Subscription = new Subscription();
  private breakpointSubscription: Subscription = new Subscription();
  private onboardingSubscription: Subscription = new Subscription();
  private migrationModeSubscription: Subscription = new Subscription();
  private releaseNewsSubscription: Subscription = new Subscription();
  private unreadSubscription: Subscription = new Subscription();
  isMobile$: Observable<boolean>;
  isMobile = false;
  // Native (Capacitor) tablets in landscape render the desktop sidebar. Their
  // WebView latches :hover after a tap, so the unfoldable rail expands and
  // stays open. This flag lets the stylesheet neutralize hover-to-expand
  // deterministically, without relying on flaky `@media (hover)` reporting.
  readonly isNative = Capacitor.isNativePlatform();
  mobileNavOpen = false;
  readonly mobileHiddenNavUrls = [
    '/pages/download',
    '/pages/ai_management',
    '/pages/support',
    '/pages/payments',
    '/pages/order_history',
    '/pages/settings-management'
  ];
  currentLanguage: string;
  mobileAccountItems: MobileNavActionItem[] = [];
  languageOptions: MobileNavLanguageItem[] = [];
  globalUser = global_user;
  onboardingStatus: OnboardingStatus | null = null;
  onboardingActive = false;
  releaseNewsVisible = false;
  unseenReleases: ReleaseNewsItem[] = [];
  get activeRelease(): ReleaseNewsItem | null {
    return this.unseenReleases[0] || null;
  }
  private readonly supportedLanguages = environment.supportedLanguages || ['en', 'tr', 'es', 'el', 'de'];
  private readonly languageLabels: Record<string, string> = {
    en: 'HEADER.LANGUAGE.ENGLISH',
    tr: 'HEADER.LANGUAGE.TURKISH',
    es: 'HEADER.LANGUAGE.SPANISH',
    el: 'HEADER.LANGUAGE.GREEK',
    de: 'HEADER.LANGUAGE.GERMAN',
  };
  private readonly languageIcons: Record<string, any> = {
    en: cifUs,
    tr: cifTr,
    es: cifEs,
    el: cifGr,
    de: cifDe,
  };

  constructor(
    private translate: TranslateService,
    private authService: AuthService,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private onboardingService: OnboardingService,
    private migrationModeService: MigrationModeService,
    private backButtonService: BackButtonService,
    private releaseNewsService: ReleaseNewsService,
    private tokenStorage: TokenStorageService,
    private channelUnread: ChannelUnreadService
  ) {
    this.translate.addLangs(this.supportedLanguages);
    this.currentLanguage =
      this.translate.currentLang || this.translate.defaultLang || environment.language;
    this.languageOptions = this.supportedLanguages.map((code) => ({
      code,
      label: this.languageLabels[code] || code,
      icon: this.languageIcons[code],
    }));
    // Navbar-only layout switch — this flag drives the sidebar / mobile-nav /
    // header structure in this shell, NOT page content. Each page keeps its own
    // internal < 992px breakpoint, so pages still render their desktop versions
    // at tablet widths.
    //
    // The native (Capacitor) app is only ever installed on phones/tablets, which
    // must ALWAYS use the small-screen (mobile navbar) layout — never the desktop
    // sidebar. So we skip the responsive queries entirely and pin isMobile=true.
    // This is fully deterministic: width, orientation, and `pointer` (which a
    // paired mouse/keyboard folio flips to `fine`) can no longer make two
    // identical tablets disagree.
    //
    // In the browser we can't assume a phone/tablet, so it stays responsive: the
    // mobile navbar for phones & portrait tablets (< lg / 991.98px) and touch
    // tablets up to 1366px; non-touch laptops report a fine pointer and keep the
    // desktop sidebar. observe([...]) matches when ANY query matches (an OR).
    this.isMobile$ = this.isNative
      ? of(true)
      : this.breakpointObserver
          .observe(['(max-width: 991.98px)', '(max-width: 1366px) and (pointer: coarse)'])
          .pipe(
            map((state) => state.matches),
            shareReplay({ refCount: true, bufferSize: 1 })
          );
  }

  ngOnInit(): void {
    // C28/C29: the account entries come from the same list as the desktop dropdown.
    this.mobileAccountItems = [
      { label: 'USER_DROPDOWN.MESSAGES', icon: 'cilEnvelopeOpen', url: '/pages/notifications' },
      ...visibleAccountMenu(this.tokenStorage),
      { label: 'USER_DROPDOWN.LOGOUT', icon: 'cilAccountLogout', action: 'logout', danger: true },
    ];

    this.migrationModeSubscription = this.migrationModeService.mode$.subscribe((mode) => {
      if (mode === 'modern') {
        this.refreshOnboardingStatus();
      } else {
        this.onboardingStatus = null;
        this.onboardingActive = false;
      }
      this.loadNavItems();
    });

    // Load initial navigation items
    this.loadNavItems();

    this.unreadSubscription = this.channelUnread.counts$.subscribe((counts) => {
      this.unreadCounts = counts;
      this.channelUnreadTotal = Object.values(counts).reduce((sum, n) => sum + n, 0);
      this.navItems = withUnreadBadges(this.baseNavItems, counts);
    });

    // Subscribe to language change events
    this.languageSubscription = this.translate.onLangChange.subscribe(() => {
      this.currentLanguage = this.translate.currentLang || environment.language;
      this.loadNavItems(); // Reload navigation items on language change
    });

    this.breakpointSubscription = this.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
      if (!isMobile) {
        this.mobileNavOpen = false;
      }
    });

    this.onboardingSubscription = this.onboardingService.status$.subscribe(
      (status) => {
        if (this.migrationModeService.getCurrentMode() !== 'modern') {
          return;
        }
        if (!status) {
          return;
        }
        this.onboardingStatus = status;
        this.onboardingActive = !status.onboarding_completed;
        this.loadNavItems();
      }
    );

    if (this.migrationModeService.getCurrentMode() === 'legacy') {
      this.migrationModeService.resolveModeForCurrentUser().subscribe({
        error: () => {
          // Keep legacy mode on failure
        }
      });
    }

    this.releaseNewsSubscription = this.releaseNewsService.unseen().subscribe({
      next: ({ releases }) => {
        this.unseenReleases = releases;
        this.releaseNewsVisible = releases.length > 0;
      },
      error: () => {
        // Release news must never prevent the customer dashboard from loading.
      },
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe from language changes to avoid memory leaks
    this.languageSubscription.unsubscribe();
    this.breakpointSubscription.unsubscribe();
    this.onboardingSubscription.unsubscribe();
    this.migrationModeSubscription.unsubscribe();
    this.releaseNewsSubscription.unsubscribe();
    this.unreadSubscription.unsubscribe();
    this.channelUnread.watch([]);
  }

  private loadNavItems(): void {
    const companyType = this.globalUser?.user?.company_type || null;
    // Dynamically load the appropriate nav items based on the environment
    const mode = this.migrationModeService.getCurrentMode();
    const items = environment.isAdmin
      ? getAdminNavItems(this.translate) // Admin nav items
      : filterNavByAccess(
          getNavItems(this.translate, mode, companyType, this.onboardingActive),
          (pageKey) => this.tokenStorage.canSeePage(pageKey)
        ); // Regular nav items, minus pages this member's access hides

    if (mode === 'modern' && this.onboardingActive && this.onboardingStatus) {
      const allowedRoutes = this.onboardingService.getAllowedRoutes(this.onboardingStatus);
      this.setNavItems(this.filterNavItemsForOnboarding(items, allowedRoutes));
      return;
    }

    this.setNavItems(items);
  }

  private setNavItems(items: any[]): void {
    this.baseNavItems = items;
    this.navItems = withUnreadBadges(items, this.unreadCounts);
    this.channelUnread.watch(items.map((item) => item.url).filter((url) => typeof url === 'string'));
  }

  private refreshOnboardingStatus(): void {
    this.onboardingService.getStatus().subscribe({
      error: () => {
        this.onboardingStatus = null;
        this.onboardingActive = false;
      }
    });
  }

  private filterNavItemsForOnboarding(items: any[], allowedRoutes: string[]): any[] {
    const allowed = allowedRoutes.map((route) => this.onboardingService.normalizeUrl(route));
    const filtered: any[] = [];
    let sectionTitle: any | null = null;
    let sectionItems: any[] = [];

    const flushSection = () => {
      if (sectionTitle) {
        if (sectionItems.length) {
          filtered.push(sectionTitle, ...sectionItems);
        }
      } else if (sectionItems.length) {
        filtered.push(...sectionItems);
      }
      sectionTitle = null;
      sectionItems = [];
    };

    items.forEach((item) => {
      if (item.title) {
        flushSection();
        sectionTitle = item;
        return;
      }

      if (!item.url) {
        return;
      }

      const normalized = this.onboardingService.normalizeUrl(item.url);
      const isAllowed = allowed.some((route) => normalized.startsWith(route));
      if (isAllowed) {
        sectionItems.push(item);
      }
    });

    flushSection();
    return filtered;
  }

  toggleMobileNav(): void {
    this.mobileNavOpen = !this.mobileNavOpen;
    if (Capacitor.isNativePlatform()) {
      if (this.mobileNavOpen) {
        // Drawer açıldı → geri tuşu için handler kaydı
        this.backButtonService.pushHandler(() => {
          this.mobileNavOpen = false;
          return true;
        });
      } else {
        // Uygulama içinden kapatıldı → handler'ı temizle
        this.backButtonService.popHandler();
      }
    }
  }

  closeMobileNav(): void {
    if (this.mobileNavOpen && Capacitor.isNativePlatform()) {
      this.backButtonService.popHandler();
    }
    this.mobileNavOpen = false;
  }

  handleMobileNavAction(action: string): void {
    if (action === 'logout') {
      this.authService.logout();
      this.router.navigate(['/login']);
    }
    if (action === 'switch-profile') {
      this.authService.switchProfile();
    }
  }

  changeLanguage(language: string): void {
    if (!this.supportedLanguages.includes(language)) {
      return;
    }
    this.currentLanguage = language;
    this.translate.use(language);
  }

  dismissReleaseNews(): void {
    const ids = this.unseenReleases.map((release) => release.id);
    this.unseenReleases = [];
    this.releaseNewsVisible = false;
    if (ids.length) {
      this.releaseNewsService.markSeen(ids).subscribe({ error: () => {} });
    }
  }

  openReleaseHistory(): void {
    this.dismissReleaseNews();
    this.router.navigate(['/pages/release-news']);
  }
}
